import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function SavingsCalculator() {
  const { t } = useTranslation();
  const [targetAmount, setTargetAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [months, setMonths] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: '/' },
      { name: t('categories.finance'), url: '/finance' },
      { name: "Savings Calculator", url: '/calculator/savings' }
    ], lang),
    (lang) => calculatorToolSchema(
      "Savings Calculator",
      "Calculate monthly savings needed to reach your financial goal. Enter target amount, interest rate, and time period to find required monthly contributions.",
      "https://calclab.app/calculator/savings",
      lang
    )
  ]);

  const calculate = () => {
    const FV = parseFloat(targetAmount);
    const r = parseFloat(interestRate) / 100 / 12;
    const n = parseFloat(months);

    if (isNaN(FV) || isNaN(r) || isNaN(n) || FV <= 0 || r < 0 || n <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    const PMT = (FV * r) / (Math.pow(1 + r, n) - 1);
    setResult(PMT);
    toast.success("Monthly savings calculated!");
  };

  const reset = () => {
    setTargetAmount("");
    setInterestRate("");
    setMonths("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Savings Calculator</CardTitle>
          <CardDescription>
            Calculate monthly savings needed to reach your goal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="targetAmount">Target Amount ($)</Label>
            <Input
              id="targetAmount"
              type="number"
              placeholder="50000"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="interestRate">Annual Interest Rate (%)</Label>
            <Input
              id="interestRate"
              type="number"
              placeholder="4"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="months">Time Period (months)</Label>
            <Input
              id="months"
              type="number"
              placeholder="60"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg break-all overflow-wrap-anywhere">
                    <strong>Monthly Savings Needed:</strong> ${result.toFixed(2)}
                  </p>
                  <CopyResultButton value={result.toFixed(2)} />
                </div>
              </div>
              
              <CalculatorSuperCalcCTA config={calculatorCTAs["savings"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
